import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { addressApi } from '../api/endpoints.ts';
import { useAppConfig } from './turnstile.tsx';
import { Field, classes } from './ui.tsx';
import {
  ADDRESS_CONTEXT_DEBOUNCE_MS,
  ADDRESS_QUERY_DEBOUNCE_MS,
  ADDRESS_QUERY_MIN_LENGTH,
} from '@shared/constants/index.ts';
import type { AddressSuggestion } from '@shared/types/index.ts';

/**
 * The street-address box, with suggestions when the deployment has a provider
 * for them (see services/address-lookup.ts).
 *
 * It is a text input first and a picker second, and that ordering is the whole
 * design. Typing is never intercepted: what is in the box is what gets saved,
 * Tab moves on, and a rural address the provider has never heard of is entered
 * exactly as it always was. Choosing a suggestion is a shortcut that fills the
 * city, state and postcode boxes as well, which is the part that actually saves
 * time.
 *
 * On a deployment with no provider configured this is a plain text input that
 * asks for nothing and behaves exactly as the field did before it existed.
 * That case shares this component's markup rather than returning a different
 * tree: `/config` resolves a moment after the form is usable, and swapping one
 * input for another underneath somebody who had started typing would take the
 * focus and the keystrokes with it.
 *
 * Keyboard and screen-reader behavior follows the APG combobox pattern with a
 * listbox popup (spec §27): the input keeps focus throughout and
 * `aria-activedescendant` moves the reading position, so arrow keys work
 * without the focus ring ever leaving the box.
 */

/**
 * The rest of the address, as the form currently holds it.
 *
 * Sent with the lookup because the free provider needs it. The Census matcher
 * identifies an address nationally rather than completing a fragment, and
 * "123 Main St" exists in hundreds of towns — on its own it matches nothing.
 * Whatever the estimator has already filled in narrows it to something
 * answerable, and a provider that does complete fragments is only helped by
 * the same context.
 */
export interface AddressContext {
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface AddressAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Called when a suggestion is taken, with the rest of the address. */
  onSelect: (suggestion: AddressSuggestion) => void;
  context?: AddressContext;
  hint?: string;
  error?: string;
  disabled?: boolean;
}

/** What one lookup asks about: the street, plus whatever narrows it down. */
interface LookupQuery extends AddressContext {
  street: string;
}

/**
 * The address a suggestion was taken for: the street it put in the box, and
 * the locality the form was left holding once it had applied the rest.
 *
 * The locality is recorded a render late — `null` until then — because what
 * the boxes end up holding is the parent's business and not this component's.
 * A form may take all of what a suggestion offers, some of it, or none, so
 * neither what was offered nor what was there beforehand reliably describes
 * where the address stands afterwards. Only the boxes themselves do, and only
 * once the parent has had its render.
 */
interface AcceptedAddress {
  street: string;
  locality: AddressContext | null;
}

/** Whether two localities would put the same question to the provider. */
function sameLocality(one: AddressContext, other: AddressContext): boolean {
  return (
    (one.city ?? '') === (other.city ?? '') &&
    (one.state ?? '') === (other.state ?? '') &&
    (one.postalCode ?? '') === (other.postalCode ?? '')
  );
}

export default function AddressAutocomplete({
  label,
  value,
  onChange,
  onSelect,
  context,
  hint,
  error,
  disabled,
}: AddressAutocompleteProps) {
  const { t } = useTranslation();
  const config = useAppConfig();
  const available = config.data?.addressAutocomplete === true;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [debounced, setDebounced] = useState<LookupQuery>(() => ({ street: value }));
  /**
   * Whether anyone has actually typed in this box.
   *
   * Editing an existing property loads its stored address into the field, and
   * without this the form would look that address up the moment it opened:
   * a lookup spent, and a customer's address handed to a third party, to fill
   * a list nobody asked for and nobody would see. Focus is not enough either —
   * tabbing past the field is not a request for suggestions.
   */
  const [edited, setEdited] = useState(false);
  /**
   * The address as it stood when a suggestion was last taken. Held so the list
   * does not immediately reopen offering the very thing that was just chosen —
   * the boxes changed, which is otherwise indistinguishable from typing.
   *
   * The whole address and not just the street, because the locality is part of
   * what gets looked up. Correcting the town of a matched address by hand
   * makes it a different address — the form says so itself by dropping the
   * coordinates — and it has to be lookupable again, or the box goes quiet on
   * an address nobody has finished with.
   */
  const [accepted, setAccepted] = useState<AcceptedAddress | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);

  /*
    The context is watched, not merely read whenever a street lookup happens to
    fire. Reading it through a ref was cheaper, and it made the feature useless
    on the default provider: an address is typed street first, Census cannot
    match a street on its own, so the one lookup that ever went out went out
    without a city and came back empty — and filling in the City box, the very
    thing that would have made that lookup answerable, scheduled nothing at
    all. Suggestions then never appeared however long anybody
    waited, which reads as "the address box does not work" rather than as a
    provider that needs one more field.

    Watching them is not free, and the wait is where that is paid for. A
    trailing debounce spends a lookup per keystroke on anybody who types slower
    than it waits, and the locality is three more places to type slowly —
    a town, a state and a postcode, which on their own could put a single
    property through seventeen lookups against an hourly cap of two hundred. So
    a change in one of them waits longer than a change in the street box does:
    nobody is watching the city box for a list, and by the time they are back
    at the street the refresh has landed either way.
  */
  const { city, state, postalCode } = context ?? {};
  /** The street the last scheduled lookup was for — see the delay below. */
  const lastStreet = useRef(debounced.street);

  useEffect(() => {
    // Which box the change came from is the whole of it: the street is being
    // watched for an answer, the boxes below it are not.
    const delay = value === lastStreet.current ? ADDRESS_CONTEXT_DEBOUNCE_MS : ADDRESS_QUERY_DEBOUNCE_MS;
    const timer = setTimeout(() => {
      lastStreet.current = value;
      setDebounced({ street: value, city, state, postalCode });
    }, delay);
    return () => clearTimeout(timer);
  }, [value, city, state, postalCode]);

  /*
    Where the address came to rest after a suggestion was taken, read off the
    boxes once the parent has filled them.

    Recording instead what the suggestion offered would misjudge both ends of
    it. A form that takes less than all of a match goes on holding its own
    town, which would then read as a correction and send a lookup nobody asked
    for. And a match that normalizes a town it was given — Springfield to
    SPRINGFIELD — leaves the estimator who puts their own spelling back
    holding a value the offer never mentioned: a real correction, on an address
    whose coordinates the form has just dropped, which has to be lookupable
    again or those coordinates cannot be got back.
  */
  useEffect(() => {
    if (accepted === null || accepted.locality !== null) return;
    setAccepted({ street: accepted.street, locality: { city, state, postalCode } });
  }, [accepted, city, state, postalCode]);

  const query = debounced.street.trim();
  /**
   * Whether the list on screen still belongs to a previous keystroke.
   *
   * For the debounce interval after a character is typed, the results in hand
   * were fetched for an address that is no longer on the form. Offering them
   * is the one way this control can overwrite all four address fields with
   * something the estimator did not ask for — by Enter on a highlight they
   * cannot see, or by a click on a row that was under the pointer when the
   * list refreshed. The list is therefore hidden until it agrees with the form
   * again, which makes "what is listed matches what is typed" true at every
   * moment.
   *
   * Every box a lookup carries counts here, not only the street: a list
   * matched in the town that was in the City box a moment ago is exactly as
   * out of date as one matched against a half-typed street, and the town is
   * the box somebody would have gone off to correct.
   */
  const stale =
    value.trim() !== query ||
    (city ?? '') !== (debounced.city ?? '') ||
    (state ?? '') !== (debounced.state ?? '') ||
    (postalCode ?? '') !== (debounced.postalCode ?? '');
  /**
   * Whether the form still holds, untouched, the address that was last taken
   * from the list. While it does there is nothing to ask about: the answer is
   * the row that was chosen.
   */
  const settled =
    accepted !== null &&
    query === accepted.street &&
    // Still settling: the parent has not had the render in which it applies
    // the rest of the address, so there is nothing yet to have been corrected.
    (accepted.locality === null || sameLocality(accepted.locality, { city, state, postalCode }));
  const enabled = available && !disabled && edited && query.length >= ADDRESS_QUERY_MIN_LENGTH && !settled;

  const suggestions = useQuery({
    queryKey: ['address-suggest', query, debounced.city ?? '', debounced.state ?? '', debounced.postalCode ?? ''],
    queryFn: () =>
      addressApi.suggest({
        q: query,
        city: debounced.city,
        state: debounced.state,
        postalCode: debounced.postalCode,
      }),
    // Never while the form has already moved on from what was last scheduled.
    // The lookup is for `debounced`, and `enabled` can turn on ahead of it —
    // correcting the town of a taken address does exactly that — which would
    // spend a request on the address as it stood before the correction, for a
    // list `stale` would refuse to show anyway.
    enabled: enabled && !stale,
    // The same fragment typed again — backing up over a typo, or a second
    // property on the same street — is the common case, and the answer does
    // not change minute to minute.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const items = enabled && !stale ? (suggestions.data?.items ?? []) : [];

  // Belt and braces alongside `stale`: the highlight is the part nothing on
  // screen would reveal, so it is cleared the instant any box the lookup
  // carries changes rather than left to the list going away.
  useEffect(() => {
    setActiveIndex(-1);
  }, [value, city, state, postalCode]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const expanded = open && items.length > 0;

  function take(suggestion: AddressSuggestion) {
    setAccepted({ street: suggestion.addressLine1, locality: null });
    lastStreet.current = suggestion.addressLine1;
    setDebounced((current) => ({ ...current, street: suggestion.addressLine1 }));
    setOpen(false);
    setActiveIndex(-1);
    onSelect(suggestion);
  }

  return (
    <div className="relative" ref={wrapper}>
      <Field label={label} hint={hint} error={error}>
        {({ id, describedBy, invalid }) => (
          <>
            <input
              id={id}
              type="text"
              // The browser's own address autofill is switched off only where
              // this control has a list of its own to show — the two dropdowns
              // would sit on top of each other and disagree. With no provider
              // configured there is nothing to collide with, and taking away
              // the saved addresses somebody's browser already offers would
              // make the field worse than the plain one it replaced.
              autoComplete={available ? 'off' : 'address-line1'}
              // Only a combobox where there is something to suggest. Announcing
              // one on a deployment with no provider promises a list that will
              // never arrive.
              role={available ? 'combobox' : undefined}
              aria-autocomplete={available ? 'list' : undefined}
              aria-expanded={available ? expanded : undefined}
              aria-controls={available ? `${id}-listbox` : undefined}
              aria-activedescendant={activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className="field-control"
              disabled={disabled}
              value={value}
              onChange={(event) => {
                setAccepted(null);
                setEdited(true);
                setOpen(true);
                onChange(event.target.value);
              }}
              onFocus={() => setOpen(true)}
              // Focus can leave without a Tab keystroke or a pointer press —
              // assistive-technology navigation and programmatic focus both do
              // it — and neither of the other close paths would run, leaving
              // the popup hanging over the fields below. `relatedTarget` is
              // where focus is going, so a move to something inside this
              // control is not a departure. A pointer press on an option never
              // reaches here at all: its `mousedown` is prevented, so the
              // input keeps focus until `take` closes the list itself.
              onBlur={(event) => {
                if (!wrapper.current?.contains(event.relatedTarget)) setOpen(false);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setOpen(true);
                  if (items.length > 0) setActiveIndex((current) => (current + 1) % items.length);
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  if (items.length > 0) setActiveIndex((current) => (current <= 0 ? items.length - 1 : current - 1));
                } else if (event.key === 'Enter') {
                  const chosen = items[activeIndex];
                  // Only swallowed when it is actually choosing something.
                  // Otherwise Enter still submits the form, which is what it
                  // does in every other field on this page.
                  if (expanded && chosen) {
                    event.preventDefault();
                    take(chosen);
                  }
                } else if (event.key === 'Escape') {
                  if (expanded) event.stopPropagation();
                  setOpen(false);
                  setActiveIndex(-1);
                } else if (event.key === 'Tab') {
                  setOpen(false);
                }
              }}
            />

            {expanded ? (
              <ul
                id={`${id}-listbox`}
                role="listbox"
                aria-label={t('addressLookup.suggestionsLabel')}
                className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-[color:var(--color-line)] bg-white shadow-lg"
              >
                {items.map((suggestion, index) => (
                  <li
                    key={suggestion.id}
                    id={`${id}-option-${index}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    // Activated on `click`, so the ways a click can arrive
                    // without a mouse still work: a mobile screen reader's
                    // double tap and a voice-control "click that" both
                    // synthesize a bare click, and an `li` has no native
                    // activation to fall back on.
                    //
                    // `mousedown` is still handled, but only to keep the
                    // pointer press from blurring the input — a blur that
                    // closed the list would unmount the row before its click
                    // landed, which is what made mousedown tempting in the
                    // first place. Prevented, not acted on, so one press is
                    // one selection.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => take(suggestion)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={classes(
                      'cursor-pointer px-3 py-2 text-sm',
                      index === activeIndex ? 'bg-[color:var(--color-brand-soft)]' : '',
                    )}
                  >
                    {suggestion.label}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </Field>

      {/*
        Sighted users can see the list appear; without this nobody else learns
        that it did, or that a lookup found nothing (spec §27).
      */}
      <p className="sr-only" role="status">
        {enabled && !stale && suggestions.isFetched
          ? items.length > 0
            ? t('addressLookup.suggestionsAvailable', { count: items.length })
            : t('addressLookup.noSuggestions')
          : ''}
      </p>
    </div>
  );
}
